import React, { useState, useEffect } from 'react';
import Select, {
  Props as SelectProps,
  GroupBase,
  MultiValue,
  SingleValue,
  ActionMeta,
  OptionsOrGroups,
  StylesConfig,
} from 'react-select';
import CreatableSelect from 'react-select/creatable';

// Define the option type
export interface SelectOption {
  value: string | number;
  label: string;
  isDisabled?: boolean;
}

// Extend the SelectProps to include our custom props
interface CustomSelectProps
  extends Omit<
    SelectProps<SelectOption, boolean, GroupBase<SelectOption>>,
    'options' | 'onChange'
  > {
  options: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
  isMulti?: boolean;
  placeholder?: string;
  isClearable?: boolean;
  isSearchable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;
  required?: boolean;
  /**
   * When true, render react-select's CreatableSelect so the user can type new
   * options on the fly (e.g. custom units of measure).
   */
  creatable?: boolean;
  /** Builds the "Create …" option label when `creatable` is true. */
  formatCreateLabel?: (inputValue: string) => React.ReactNode;
  /** Fired when the user picks the "Create …" entry. Only used when `creatable` is true. */
  onCreateOption?: (inputValue: string) => void;
  onChange?: (
    value: SingleValue<SelectOption> | MultiValue<SelectOption> | null,
    actionMeta: ActionMeta<SelectOption>,
  ) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  customComponent?: React.ComponentType<any>;
}

const ReactSelect: React.FC<CustomSelectProps> = ({
  options,
  isMulti = false,
  placeholder = 'Select an option...',
  isClearable = true,
  isSearchable = true,
  isDisabled = false,
  isLoading = false,
  error = false,
  helperText,
  label,
  required = false,
  creatable = false,
  onChange,
  onBlur,
  onFocus,
  className,
  customComponent,
  ...restProps
}) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };

    // Initial check
    checkDarkMode();

    // Create a MutationObserver to watch for class changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          checkDarkMode();
        }
      });
    });

    // Start observing
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Cleanup
    return () => observer.disconnect();
  }, []);
  const handleChange = (
    value: SingleValue<SelectOption> | MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>,
  ) => {
    if (onChange) {
      onChange(value, actionMeta);
    }
  };

  // Determine if there is a selected value to toggle vertical alignment for multi-select
  const valueProp = (restProps as any)?.value;
  const hasValue = Array.isArray(valueProp)
    ? valueProp.length > 0
    : !!valueProp;

  if (customComponent) {
    const CustomComponent = customComponent;
    return (
      <div className={className}>
        <CustomComponent
          options={options}
          value={restProps.value}
          onChange={onChange}
          placeholder={placeholder}
          className={error ? 'border-red-500' : ''}
          isDisabled={isDisabled}
          name={restProps.name}
          menuPortalTarget={(restProps as any).menuPortalTarget}
          menuPosition={(restProps as any).menuPosition}
        />
        {helperText && (
          <p
            className={`mt-1 text-xs ${error ? 'text-red-600' : 'text-gray-500'}`}>
            {helperText}
          </p>
        )}
      </div>
    );
  }

  const SelectComponent: any = creatable ? CreatableSelect : Select;

  const styles: StylesConfig<SelectOption, boolean, GroupBase<SelectOption>> = {
    menu: (props) => ({
      ...props,
      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
      border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: '40px',
      height: isMulti ? 'auto' : provided.height,
      maxHeight: isMulti ? 'none' : '40px',
      alignItems: isMulti
        ? hasValue
          ? 'flex-start'
          : 'center'
        : provided.alignItems,
      border: error
        ? state.isFocused
          ? '1px solid #ef4444'
          : '1px solid #ef4444'
        : state.isFocused
          ? '1px solid #6366f1'
          : isDarkMode
            ? '1px solid #4b5563'
            : '1px solid #d1d5db',
      borderRadius: '6px',
      boxShadow: error
        ? state.isFocused
          ? '0 0 0 1px #ef4444'
          : 'none'
        : state.isFocused
          ? '0 0 0 1px #6366f1'
          : 'none',
      transition: 'all 0.2s ease',
      backgroundColor: state.isDisabled
        ? isDarkMode
          ? '#374151'
          : '#f3f4f6'
        : isDarkMode
          ? '#1f2937'
          : '#ffffff',
      '&:hover': {
        border: error
          ? '1px solid #ef4444'
          : isDarkMode
            ? '1px solid #6b7280'
            : '1px solid #9ca3af',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      minHeight: '40px',
      flexWrap: isMulti && hasValue ? 'wrap' : 'nowrap',
      alignItems: isMulti
        ? hasValue
          ? 'flex-start'
          : 'center'
        : provided.alignItems,
      paddingTop: isMulti && hasValue ? 4 : provided.paddingTop,
      paddingBottom: isMulti && hasValue ? 4 : provided.paddingBottom,
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
      color: isDarkMode ? '#f9fafb' : '#111827',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDarkMode ? '#6b7280' : '#9ca3af',
      margin: 0,
      fontSize: '14px',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? '#f9fafb' : '#111827',
      margin: 0,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#374151' : '#dbeafe',
      borderRadius: '6px',
      margin: '2px 4px 2px 0',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: isDarkMode ? '#f9fafb' : '#1e40af',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: isDarkMode ? '#9ca3af' : '#1e40af',
      '&:hover': {
        backgroundColor: isDarkMode ? '#4b5563' : '#bfdbfe',
        color: isDarkMode ? '#f9fafb' : '#1e40af',
      },
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      padding: '8px 12px',
      cursor: 'pointer',
      backgroundColor: state.isSelected
        ? isDarkMode
          ? '#4338ca'
          : '#dbeafe'
        : state.isFocused
          ? isDarkMode
            ? '#374151'
            : '#eff6ff'
          : isDarkMode
            ? '#1f2937'
            : '#ffffff',
      color: state.isSelected
        ? isDarkMode
          ? '#ffffff'
          : '#1e40af'
        : isDarkMode
          ? '#f9fafb'
          : '#111827',
      '&:hover': {
        backgroundColor: state.isSelected
          ? isDarkMode
            ? '#3730a3'
            : '#bfdbfe'
          : isDarkMode
            ? '#374151'
            : '#f9fafb',
        color: state.isSelected
          ? isDarkMode
            ? '#ffffff'
            : '#1e40af'
          : isDarkMode
            ? '#f9fafb'
            : '#111827',
      },
      ...(state.isDisabled && {
        color: isDarkMode ? '#6b7280' : '#9ca3af',
        cursor: 'not-allowed',
      }),
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      color: isDarkMode ? '#9ca3af' : '#6b7280',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#4b5563' : '#d1d5db',
    }),
    group: (provided) => ({
      ...provided,
      paddingTop: 0,
      paddingBottom: 0,
    }),
    groupHeading: (provided) => ({
      ...provided,
      fontSize: '11px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: isDarkMode ? '#9ca3af' : '#6b7280',
      backgroundColor: isDarkMode ? '#111827' : '#f3f4f6',
      padding: '8px 12px',
      margin: 0,
      borderTop: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
    }),
  };

  return (
    <div className={`${className} relative overflow-visible`}>
      <SelectComponent
        options={options}
        isMulti={isMulti}
        placeholder={placeholder}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isDisabled={isDisabled}
        isLoading={isLoading}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        styles={styles}
        classNamePrefix="react-select"
        menuPortalTarget={document.body}
        menuPlacement="auto"
        {...restProps}
      />
    </div>
  );
};

export default ReactSelect;
